import React, { useState, useEffect } from 'react';
import api from '../api/api';
import AppLayout from './AppLayout';
import PageTitle from './PageTitle';
import { AdBanner } from './GoogleAdsense';
import '../styles/tsumugi-design.css';
import '../styles/empathy-feed.css';
import '../styles/tab-navigation.css';

const EmpathyFeed = ({ userId = null }) => {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);

  // ページタイトル設定
  useEffect(() => {
    document.title = '共感フィード | つむぎ';
  }, []);

  useEffect(() => {
    fetchPosts();
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const params = {
        limit: 20,
        sort: 'latest'
      };
      if (selectedCategory) {
        params.category = selectedCategory;
      }

      const response = await api.get('/api/empathy-feed', { params });

      if (response.data.success) {
        setPosts(response.data.posts);
      }
    } catch (error) {
      console.error('Failed to fetch empathy feed:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/api/empathy-feed/categories');
      if (response.data.success) {
        setCategories(response.data.categories);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const handleEmpathize = async (postId) => {
    try {
      const response = await api.post(
        `/api/empathy-feed/${postId}/empathize`,
        { userId }
      );

      if (response.data.success) {
        setPosts(posts.map(post => {
          if (post.id === postId) {
            return {
              ...post,
              empathyCount: response.data.newCount,
              userHasEmpathized: response.data.action === 'added'
            };
          }
          return post;
        }));
      }
    } catch (error) {
      console.error('Empathy reaction error:', error);
    }
  };

  const formatTimeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return 'たった今';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}分前`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}時間前`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}日前`;
    return date.toLocaleDateString('ja-JP', { month: 'long', day: 'numeric' });
  };

  return (
    <AppLayout>
      {/* ページタイトル */}
      <PageTitle
        title="みんなの声"
        subtitle="橋本病の仲間とつながろう"
        icon="❤️"
        mascot={<img src="/tsumugi.png" alt="つむぎ" className="page-title__mascot-img" />}
      />

      {/* フィルター・ソート */}
      <div className="empathy-filter-bar">
        <button
          className={`tsumugi-btn empathy-filter-btn ${!selectedCategory ? 'tsumugi-btn-primary' : 'tsumugi-btn-outline'}`}
          onClick={() => setSelectedCategory(null)}
        >
          すべて
        </button>
        {categories.map(cat => (
          <button
            key={cat.name}
            className={`tsumugi-btn empathy-filter-btn ${selectedCategory === cat.name ? 'tsumugi-btn-primary' : 'tsumugi-btn-outline'}`}
            onClick={() => setSelectedCategory(cat.name)}
          >
            {cat.name} ({cat.count})
          </button>
        ))}
      </div>

      {/* 投稿リスト */}
      <div className="tsumugi-container pt-4 empathy-feed__posts-container">
        {loading ? (
          <div className="empathy-feed__loading">
            <p className="tsumugi-body">読み込み中...</p>
          </div>
        ) : posts.length === 0 ? (
          <div className="empathy-feed__empty">
            <p className="tsumugi-body">まだ投稿がありません</p>
          </div>
        ) : (
          posts.map((post, index) => (
            <React.Fragment key={post.id}>
              <EmpathyPostCard
                post={post}
                onEmpathize={handleEmpathize}
                formatTimeAgo={formatTimeAgo}
              />

              {/* 広告: 3投稿ごとに表示 */}
              {(index + 1) % 3 === 0 && index < posts.length - 1 && (
                <div className="empathy-feed__ad-container">
                  <AdBanner />
                </div>
              )}
            </React.Fragment>
          ))
        )}
      </div>
    </AppLayout>
  );
};

const EmpathyPostCard = ({ post, onEmpathize, formatTimeAgo }) => {
  return (
    <div className="empathy-post tsumugi-card">
      {/* 投稿者情報 */}
      <div className="empathy-post__header mb-3">
        <div className="empathy-author-avatar">
          👤
        </div>
        <div className="empathy-post__author-info">
          <p className="tsumugi-body empathy-post__author-name">
            {post.author}
          </p>
          <div className="empathy-post__metadata">
            {post.category && (
              <span className="tsumugi-tag tsumugi-tag-lilac empathy-post__category">
                {post.category}
              </span>
            )}
            <span className="tsumugi-caption empathy-post__timestamp">
              {formatTimeAgo(post.publishedAt)}
            </span>
          </div>
        </div>
      </div>

      {/* 投稿本文 */}
      <p className="tsumugi-body mb-4 empathy-post__content">
        {post.content}
      </p>

      {/* 「わかる」ボタン */}
      <button
        className={`empathy-btn ${post.userHasEmpathized ? 'empathized' : ''}`}
        onClick={() => onEmpathize(post.id)}
      >
        <span className="empathy-icon">❤️</span>
        <span className="empathy-text">わかる</span>
        <span className="empathy-count">{post.empathyCount}</span>
      </button>

      {/* つむぎのコメント */}
      {post.tsumugiComment && (
        <div className="tsumugi-comment-box">
          <div className="flex gap-2 items-start">
            <img src="/tsumugi.png" alt="つむぎ" className="empathy-feed__tsumugi-icon" />
            <div className="flex-1">
              <p className="tsumugi-caption font-medium mb-1">
                つむぎより
              </p>
              <p className="tsumugi-body empathy-post__tsumugi-comment">
                {post.tsumugiComment}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmpathyFeed;
